import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../../config';
import { useLanguage } from '../../contexts/LanguageContext';
import CashCounter from './CashCounter';
import { openCashDrawer } from '../../utils/receiptPrinter';

export default function RegisterOpen({ theme, user, registers, onSuccess, onCancel }) {
  const { t } = useLanguage();
  const [selectedRegister, setSelectedRegister] = useState('');
  const [denominations, setDenominations] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Pre-select register if only one available
  useEffect(() => {
    if (registers.length === 1) {
      setSelectedRegister(registers[0].id);
    }
  }, [registers]);

  const handleSubmit = async () => {
    if (!selectedRegister) {
      setError(t('pleaseSelectRegister'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${BACKEND_URL}/api/registers/open`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          register_id: selectedRegister,
          user_id: user.id,
          denominations
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to open register');
      }

      openCashDrawer().catch(() => {});
      onSuccess(data);
    } catch (err) {
      console.error('Error opening register:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      maxWidth: '900px',
      margin: '0 auto',
      padding: '2rem'
    }}>
      <div style={{
        backgroundColor: theme?.colors?.surface || '#fff',
        borderRadius: '12px',
        padding: '2rem',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <h2 style={{
          margin: '0 0 1.5rem 0',
          color: theme?.colors?.text || '#000',
          fontSize: '1.5rem',
          fontWeight: 600
        }}>
          {t('openRegister')}
        </h2>

        {/* Register Selection */}
        <div style={{ marginBottom: '2rem' }}>
          <label style={{
            display: 'block',
            marginBottom: '0.5rem',
            fontWeight: 500,
            color: theme?.colors?.text || '#000'
          }}>
            {t('selectRegister')}:
          </label>
          <select
            value={selectedRegister}
            onChange={(e) => setSelectedRegister(e.target.value)}
            disabled={registers.length === 1}
            style={{
              width: '100%',
              padding: '0.75rem',
              fontSize: '1rem',
              border: `1px solid ${theme?.colors?.border || '#ddd'}`,
              borderRadius: '6px',
              backgroundColor: theme?.colors?.surface || '#fff',
              color: theme?.colors?.text || '#000'
            }}
          >
            <option value="">{t('selectOne')}</option>
            {registers.map(reg => (
              <option key={reg.id} value={reg.id} disabled={reg.current_session}>
                {reg.name} {reg.current_session ? `(${t('open')})` : ''}
              </option>
            ))}
          </select>
        </div>

        {/* Cash Counter */}
        <CashCounter
          theme={theme}
          denominations={denominations}
          onChange={setDenominations}
        />

        {/* Error Message */}
        {error && (
          <div style={{
            marginTop: '1rem',
            padding: '1rem',
            backgroundColor: theme?.colors?.errorBackground || '#fee',
            border: `1px solid ${theme?.colors?.error || '#f00'}`,
            borderRadius: '6px',
            color: theme?.colors?.error || '#f00'
          }}>
            {error}
          </div>
        )}

        {/* Action Buttons */}
        <div style={{
          marginTop: '2rem',
          display: 'flex',
          gap: '1rem',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={onCancel}
            disabled={loading}
            style={{
              padding: '0.75rem 1.5rem',
              fontSize: '1rem',
              fontWeight: 500,
              border: `1px solid ${theme?.colors?.border || '#ddd'}`,
              borderRadius: '6px',
              backgroundColor: theme?.colors?.surface || '#fff',
              color: theme?.colors?.text || '#000',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1
            }}
          >
            {t('cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || !selectedRegister}
            style={{
              padding: '0.75rem 2rem',
              fontSize: '1rem',
              fontWeight: 500,
              border: 'none',
              borderRadius: '6px',
              backgroundColor: theme?.colors?.primary || '#1976d2',
              color: theme?.colors?.buttonText || theme?.colors?.surface || '#fff',
              cursor: (loading || !selectedRegister) ? 'not-allowed' : 'pointer',
              opacity: (loading || !selectedRegister) ? 0.6 : 1
            }}
          >
            {loading ? t('opening') + '...' : t('openRegister')}
          </button>
        </div>
      </div>
    </div>
  );
}
